import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

const FREE_GIFT_KEY = "_free_gift";
const FREE_GIFT_CODE = "_code_id";
const FREE_GIFT_VALUE = "1";

const DISMISSED_PREFIX = "gift-rules:dismissed:";

/**
 * @typedef {Object} CartDiscountCode
 * @property {string} code
 * @property {boolean} applicable
 *
 * @typedef {Object} CartLine
 * @property {number} id
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number|string} [line]
 * @property {string} [key]
 * @property {string} [product_type]
 * @property {number} final_line_price
 * @property {Record<string, string> | null} properties
 *
 * @typedef {Object} CartJson
 * @property {CartLine[]} items
 * @property {CartDiscountCode[]} [discount_codes]
 * @property {number} items_subtotal_price
 * @property {number} [total_price]
 * @property {number} [item_count]
 *
 * @typedef {Object} GiftRule
 * @property {string} blockId
 * @property {number} variantId
 * @property {boolean} requiresDiscountCode
 * @property {string} codes
 * @property {string} matchMode
 * @property {number | null} thresholdCents
 * @property {number | null} startUnix
 * @property {number | null} endUnix
 */

/**
 * @param {Record<string, string> | null | undefined} properties
 * @returns {boolean}
 */
function lineHasFreeGiftProperty(properties) {
  if (!properties || typeof properties !== "object") {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(properties, FREE_GIFT_KEY);
}

/**
 * Amount used for the gift threshold: the fully discounted cart total
 * (cart.total_price, which includes order-level discount codes such as
 * Conny20) minus any free gift lines.
 *
 * Previously this summed item.final_line_price, which does NOT include
 * order-level discount codes — so a cart of 355,44€ with Conny20
 * (real total 284,36€) was still treated as 355,44€ and got the gift.
 *
 * @param {CartJson} cart
 * @returns {number}
 */
function qualifyingSubtotalCents(cart) {
  if (!cart.items?.length) {
    return 0;
  }

  let giftLinesCents = 0;

  for (const item of cart.items) {
    if (lineHasFreeGiftProperty(item.properties)) {
      giftLinesCents += item.final_line_price ?? 0;
    }
  }

  return Math.max(0, (cart.total_price ?? 0) - giftLinesCents);
}

/**
 * @param {CartLine} item
 * @returns {boolean}
 */
function lineIsGiftCard(item) {
  if (!item || typeof item !== "object") {
    return false;
  }

  if (typeof item.product_type !== "string") {
    return false;
  }

  return item.product_type.trim().toLowerCase() === "gift cards";
}

/**
 * @param {CartJson} cart
 * @returns {boolean}
 */
function containsOnlyGiftCardsAndFreeGifts(cart) {
  if (!cart.items?.length) {
    return false;
  }

  let hasQualifyingPaidLine = false;

  for (const item of cart.items) {
    if (lineHasFreeGiftProperty(item.properties)) {
      continue;
    }

    hasQualifyingPaidLine = true;

    if (!lineIsGiftCard(item)) {
      return false;
    }
  }

  return hasQualifyingPaidLine;
}

/**
 * @param {CartJson} cart
 * @param {number} variantId
 * @returns {CartLine | undefined}
 */
function findFreeGiftLine(cart, variantId) {
  if (!cart.items?.length) {
    return undefined;
  }

  for (const item of cart.items) {
    if (
      item.variant_id === variantId &&
      lineHasFreeGiftProperty(item.properties)
    ) {
      return item;
    }
  }

  return undefined;
}

/**
 * Fallback for `/cart/change.js` when `id` (line-item key) is missing: 1-based `line` index.
 *
 * @see https://shopify.dev/docs/api/ajax/reference/cart#post--locale-cart-change-js
 * @param {CartLine} item
 * @param {CartJson} cart
 * @returns {number | null}
 */
function lineIndexForChange(item, cart) {
  if (!item || !cart.items?.length) {
    return null;
  }

  let idx = cart.items.indexOf(item);
  if (idx < 0 && item.key) {
    idx = cart.items.findIndex((line) => line.key === item.key);
  }

  return idx >= 0 ? idx + 1 : null;
}

/**
 * @returns {string[]}
 */
function getSectionIdsToUpdate() {
  const cartItemsComponents = document.querySelectorAll("cart-items-component");
  /** @type {string[]} */
  const sectionIds = [];
  const seen = new Set();

  for (const item of cartItemsComponents) {
    if (!(item instanceof HTMLElement) || !item.dataset.sectionId) {
      continue;
    }

    if (seen.has(item.dataset.sectionId)) {
      continue;
    }

    seen.add(item.dataset.sectionId);
    sectionIds.push(item.dataset.sectionId);
  }

  return sectionIds;
}

/**
 * @param {string} codesRaw
 * @returns {string[]}
 */
function parseCodeTokens(codesRaw) {
  if (!codesRaw || typeof codesRaw !== "string") {
    return [];
  }

  return codesRaw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.toUpperCase());
}

/**
 * @param {string[]} appliedCodes
 * @param {string[]} tokensUpper
 * @param {'exact' | 'prefix'} mode
 * @returns {string | null}
 */
function findMatchingAppliedCode(appliedCodes, tokensUpper, mode) {
  if (tokensUpper.length === 0) {
    return null;
  }

  for (const token of tokensUpper) {
    for (const applied of appliedCodes) {
      const appliedUpper = applied.toUpperCase();
      if (mode === "prefix") {
        if (appliedUpper.startsWith(token)) {
          return applied;
        }
      } else if (appliedUpper === token) {
        return applied;
      }
    }
  }

  return null;
}

/**
 * @param {CartJson} cart
 * @returns {string[]}
 */
function applicableDiscountCodes(cart) {
  const list = cart.discount_codes;
  if (!list?.length) {
    return [];
  }

  /** @type {string[]} */
  const out = [];

  for (const d of list) {
    if (d.applicable && typeof d.code === "string") {
      out.push(d.code);
    }
  }

  return out;
}

/**
 * @param {number | null} startUnix
 * @param {number | null} endUnix
 * @returns {boolean}
 */
function inDateWindow(startUnix, endUnix) {
  const nowSec = Math.floor(Date.now() / 1000);

  if (startUnix != null && nowSec < startUnix) {
    return false;
  }

  if (endUnix != null && nowSec > endUnix) {
    return false;
  }

  return true;
}

/**
 * @extends {Component<Record<string, never>>}
 */
class GiftRulesSync extends Component {
  /** @type {boolean} */
  #syncing = false;

  /**
   * True when a cart/discount update event arrived while a sync was
   * already in flight. Without this, that event would be silently
   * dropped (see #onCartOrDiscount), leaving the gift line stale if
   * the in-flight sync read the cart before the discount had fully
   * recalculated the total.
   * @type {boolean}
   */
  #pendingSync = false;

  /** @type {GiftRule[]} */
  #rules = [];

  /** @type {CartJson | null} */
  #lastCartSnapshot = null;

  connectedCallback() {
    super.connectedCallback();

    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      return;
    }

    const el = document.getElementById(`GiftRulesConfig-${sectionId}`);
    if (!(el instanceof HTMLScriptElement) || el.type !== "application/json") {
      return;
    }

    try {
      const parsed = JSON.parse(el.textContent || "[]");
      this.#rules = Array.isArray(parsed)
        ? /** @type {GiftRule[]} */ (parsed)
        : [];
    } catch {
      this.#rules = [];
    }

    if (this.#rules.length === 0) {
      return;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartOrDiscount);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.#onCartOrDiscount
    );

    queueMicrotask(() => {
      this.#sync().catch(() => {});
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartOrDiscount
    );
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.#onCartOrDiscount
    );
  }

  #onCartOrDiscount = () => {
    if (this.#syncing) {
      // A sync is already running against a cart snapshot that may be
      // stale by the time it finishes (e.g. a discount code is still
      // being applied server-side). Queue a follow-up sync instead of
      // dropping this event, so the final state always gets re-checked.
      this.#pendingSync = true;
      return;
    }

    this.#sync().catch(() => {});
  };

  /**
   * @param {GiftRule} rule
   * @param {CartJson} cart
   * @returns {{ shouldApply: boolean; matchedCode: string | null }}
   */
  #evaluateRule(rule, cart) {
    if (containsOnlyGiftCardsAndFreeGifts(cart)) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (rule.thresholdCents == null) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (!inDateWindow(rule.startUnix, rule.endUnix)) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    const subtotal = qualifyingSubtotalCents(cart);

    // TEMP DEBUG — remove after verifying
    console.log("[gift-debug]", {
      variantId: rule.variantId,
      thresholdCents: rule.thresholdCents,
      qualifyingCents: subtotal,
      cartTotalPrice: cart.total_price,
      itemsSubtotalPrice: cart.items_subtotal_price,
      discountCodes: cart.discount_codes,
      qualifies: subtotal >= rule.thresholdCents
    });

    if (subtotal < rule.thresholdCents) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (!rule.requiresDiscountCode) {
      return {
        shouldApply: true,
        matchedCode: null
      };
    }

    const applied = applicableDiscountCodes(cart);
    const tokens = parseCodeTokens(rule.codes);
    const matchedCode = findMatchingAppliedCode(
      applied,
      tokens,
      rule.matchMode === "prefix" ? "prefix" : "exact"
    );

    return {
      shouldApply: Boolean(matchedCode),
      matchedCode
    };
  }

  /**
   * @param {number} variantId
   * @returns {string}
   */
  #dismissKey(variantId) {
    return `${DISMISSED_PREFIX}${variantId}`;
  }

  /**
   * @param {CartJson} cart
   */
  #clearDismissWhenIneligible(cart) {
    for (const rule of this.#rules) {
      if (!this.#evaluateRule(rule, cart).shouldApply) {
        sessionStorage.removeItem(this.#dismissKey(rule.variantId));
      }
    }
  }

  /**
   * User removed the promo line while still eligible — remember for this session.
   *
   * @param {CartJson | null} previous
   * @param {CartJson} current
   */
  #recordManualRemoval(previous, current) {
    if (!previous?.items?.length) {
      return;
    }

    for (const rule of this.#rules) {
      const hadLine = findFreeGiftLine(previous, rule.variantId);
      const hasLine = findFreeGiftLine(current, rule.variantId);
      const shouldHave = this.#evaluateRule(rule, current).shouldApply;

      if (hadLine && !hasLine && shouldHave) {
        sessionStorage.setItem(this.#dismissKey(rule.variantId), "1");
      }
    }
  }

  /**
   * @param {number} variantId
   * @returns {boolean}
   */
  #isDismissed(variantId) {
    return sessionStorage.getItem(this.#dismissKey(variantId)) === "1";
  }

  /**
   * @returns {Set<number>}
   */
  #ruleVariantIdSet() {
    const out = new Set();

    for (const rule of this.#rules) {
      if (Number.isFinite(rule.variantId)) {
        out.add(rule.variantId);
      }
    }

    return out;
  }

  /**
   * @param {Set<number>} activeVariantIds
   */
  #clearOrphanDismissKeys(activeVariantIds) {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(DISMISSED_PREFIX)) {
        continue;
      }

      const rawId = key.slice(DISMISSED_PREFIX.length);
      const variantId = Number(rawId);
      if (!Number.isFinite(variantId) || !activeVariantIds.has(variantId)) {
        sessionStorage.removeItem(key);
        i -= 1;
      }
    }
  }

  /**
   * @param {CartJson} cart
   * @returns {CartJson}
   */
  #cloneCartSnapshot(cart) {
    return /** @type {CartJson} */ (JSON.parse(JSON.stringify(cart)));
  }

  #sync = async () => {
    if (this.#syncing || this.#rules.length === 0) {
      return;
    }

    this.#syncing = true;

    /** @type {boolean} */
    let mutated = false;

    /** @type {Record<string, string> | undefined} */
    let mutationSections;

    try {
      const previousSnapshot = this.#lastCartSnapshot;

      let cart = await this.#fetchCart();
      if (!cart) {
        return;
      }

      this.#clearDismissWhenIneligible(cart);
      this.#recordManualRemoval(previousSnapshot, cart);
      const activeVariantIds = this.#ruleVariantIdSet();
      this.#clearOrphanDismissKeys(activeVariantIds);

      for (const item of cart.items) {
        if (!lineHasFreeGiftProperty(item.properties)) {
          continue;
        }

        if (activeVariantIds.has(item.variant_id)) {
          continue;
        }

        const removeResult = await this.#removeLine(item, cart);
        if (removeResult.ok) {
          mutated = true;
          if (removeResult.sections) {
            mutationSections = removeResult.sections;
          }
        }

        const next = await this.#fetchCart();
        if (next) {
          cart = next;
        }
      }

      for (const rule of this.#rules) {
        const evaluation = this.#evaluateRule(rule, cart);
        const shouldHave = evaluation.shouldApply;
        const line = findFreeGiftLine(cart, rule.variantId);

        if (shouldHave && !line) {
          if (this.#isDismissed(rule.variantId)) {
            continue;
          }

          const addResult = await this.#addGift(
            rule.variantId,
            evaluation.matchedCode
          );
          if (addResult.ok) {
            mutated = true;
            if (addResult.sections) {
              mutationSections = addResult.sections;
            }
          }
          const next = await this.#fetchCart();
          if (next) {
            cart = next;
          }
        } else if (!shouldHave && line) {
          const removeResult = await this.#removeLine(line, cart);

          if (removeResult.ok) {
            mutated = true;

            if (removeResult.sections) {
              mutationSections = removeResult.sections;
            }
          }
          const next = await this.#fetchCart();
          if (next) {
            cart = next;
          }
        }
      }
    } finally {
      this.#syncing = false;
    }

    const finalCart = await this.#fetchCart();
    if (finalCart) {
      this.#lastCartSnapshot = this.#cloneCartSnapshot(finalCart);
    }

    if (mutated && finalCart) {
      const itemCount =
        typeof finalCart.item_count === "number"
          ? finalCart.item_count
          : (finalCart.items?.length ?? 0);

      document.dispatchEvent(
        new CartUpdateEvent(finalCart, "gift-rules-sync", {
          itemCount,
          ...(mutationSections && { sections: mutationSections })
        })
      );
    }

    if (this.#pendingSync) {
      this.#pendingSync = false;
      // A cart/discount update arrived while we were syncing above.
      // Re-run against the latest state instead of leaving it stale.
      this.#sync().catch(() => {});
    }
  };

  /**
   * @returns {Promise<CartJson | null>}
   */
  #fetchCart = async () => {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        return null;
      }

      return /** @type {CartJson} */ (await response.json());
    } catch {
      return null;
    }
  };

  /**
   * @param {number} variantId
   * @param {string | null} [matchedDiscountCode]
   * @returns {Promise<{ ok: boolean; sections?: Record<string, string>; sectionIds?: string[] }>}
   */
  #addGift = async (variantId, matchedDiscountCode = null) => {
    const formData = new FormData();
    formData.append("id", String(variantId));
    formData.append("quantity", "1");
    formData.append(`properties[${FREE_GIFT_KEY}]`, FREE_GIFT_VALUE);
    if (
      typeof matchedDiscountCode === "string" &&
      matchedDiscountCode.trim() !== ""
    ) {
      formData.append(`properties[${FREE_GIFT_CODE}]`, matchedDiscountCode);
    }

    const sectionIds = getSectionIdsToUpdate();

    if (sectionIds.length) {
      formData.append("sections", sectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json"
      }
    });

    const data = await response.json();

    if (data.status) {
      return { ok: false };
    }

    return {
      ok: true,
      ...(sectionIds.length ? { sectionIds } : {}),
      ...(data.sections && typeof data.sections === "object"
        ? { sections: data.sections }
        : {})
    };
  };

  /**
   * @param {CartLine} item
   * @param {CartJson} cart
   * @returns {Promise<{ ok: boolean; sections?: Record<string, string>; sectionIds?: string[] }>}
   */
  #removeLine = async (item, cart) => {
    const sectionIds = getSectionIdsToUpdate();

    /** @type {Record<string, string | number>} */
    const payload = {
      quantity: 0,
      sections: sectionIds.join(","),
      sections_url: window.location.pathname
    };

    if (item.key) {
      payload.id = item.key;
    } else {
      const line = lineIndexForChange(item, cart);

      if (line == null) {
        return { ok: false };
      }

      payload.line = line;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body })
    );

    const text = await response.text();
    let parsed;

    try {
      parsed = JSON.parse(text);
    } catch {
      return { ok: false };
    }

    if (parsed.errors) {
      return { ok: false };
    }

    return {
      ok: true,
      ...(sectionIds.length ? { sectionIds } : {}),
      ...(parsed.sections && typeof parsed.sections === "object"
        ? { sections: /** @type {Record<string, string>} */ (parsed.sections) }
        : {})
    };
  };
}

if (!customElements.get("gift-rules-sync")) {
  customElements.define("gift-rules-sync", GiftRulesSync);
}